import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebaseConfig";

Modal.setAppElement("#root");

// normaliza texto para buscar sin problema de acentos / mayúsculas
const normalizeText = (str = "") =>
  str
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/* ─── estilos para react-modal ─── */
const modalStyles = {
  content: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    top: "50%",
    left: "50%",
    transform: "translate(-50%,-50%)",
    width: "28%", // un poco más compacto, puedes ajustar
    maxWidth: "520px",
    minHeight: "0", // sin altura fija grande
    padding: 18, // ligeramente menor
    borderRadius: 10,
    border: "1px solid #ccc",
    boxShadow: "0 5px 15px rgba(0,0,0,.3)",
    fontFamily: "Montserrat, sans-serif",
    boxSizing: "border-box",
  },
  overlay: {
    backgroundColor: "rgba(0,0,0,.4)",
    zIndex: 1000,
  },
  input: {
    width: "100%",
    padding: 8,
    borderRadius: 5,
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
};

const DEFAULT_COLOR = "#6366F1"; // color por defecto para nuevas carpetas

export default function SidebarGastos({
  idOrden,
  listas = [],
  setListas,
  onSelectLista = () => {},
  onListaEliminada = () => {},
  // total global que viene de GastosView
  totalCarpetas = 0,
  formatCurrency = (n) => `MXN ${Number(n || 0).toFixed(2)}`,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [listaName, setListaName] = useState("");
  const [filtro, setFiltro] = useState("");
  const [editId, setEditId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [color, setColor] = useState(DEFAULT_COLOR); // color seleccionado
  const sidebarRef = useRef(null);

  // escucha Firestore
  useEffect(() => {
    if (!idOrden) return;
    const q = query(
      collection(db, `pedidos/${idOrden}/listasGastos`),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, (snap) =>
      setListas(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [idOrden, setListas]);

  // clic fuera para cerrar dropdown
  useEffect(() => {
    const handler = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const openModal = () => setModalOpen(true);

  const closeModal = () => {
    setModalOpen(false);
    setListaName("");
    setEditId(null);
    setColor(DEFAULT_COLOR); // reset color al cerrar
  };

  const guardarLista = async () => {
    if (!listaName.trim())
      return Swal.fire("Error", "Ingresa un nombre de lista", "error");

    if (editId) {
      await updateDoc(doc(db, `pedidos/${idOrden}/listasGastos`, editId), {
        nombre: listaName,
        color: color,
      });
      Swal.fire("Actualizado", "Lista editada correctamente", "success");
    } else {
      await addDoc(collection(db, `pedidos/${idOrden}/listasGastos`), {
        nombre: listaName,
        color: color,
        createdAt: serverTimestamp(),
      });
      Swal.fire("Creada", "Lista creada exitosamente", "success");
    }
    closeModal();
  };

  const editarLista = (lista) => {
    setListaName(lista.nombre);
    setEditId(lista.id);
    setColor(lista.color || DEFAULT_COLOR); // usar color guardado o default
    setModalOpen(true);
  };

  const eliminarLista = async (id) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar carpeta?",
      text: "Esta acción eliminará la carpeta y sus productos.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;

    // borra productos
    const gastosRef = collection(
      db,
      `pedidos/${idOrden}/listasGastos/${id}/productosGastos`
    );
    const snap = await getDocs(gastosRef);
    if (!snap.empty) {
      const batch = writeBatch(db);
      snap.docs.forEach((d) => batch.delete(doc(gastosRef, d.id)));
      await batch.commit();
    }
    // borra carpeta
    await deleteDoc(doc(db, `pedidos/${idOrden}/listasGastos`, id));
    Swal.fire("Eliminado", "Carpeta eliminada", "success");
    onListaEliminada(id);
  };

  const listasFiltradas = listas.filter((l) =>
    normalizeText(l.nombre).includes(normalizeText(filtro))
  );

  return (
    <div ref={sidebarRef} className="sidebar-gastos">
      {/* === Estilos locales (diseño pulido) === */}
      <style>{`
        .sidebar-gastos{
          display:flex;
          flex-direction:column;
          height:100%;
          min-height:100%;
          box-sizing:border-box;
          background:#fff;
          border:1px solid #E6EDFF;
          border-radius:12px;
          padding:12px;
        }
        .sidebar-body{
          flex:1 1 auto;
          overflow-y:auto;
          overflow-x:hidden;
          -webkit-overflow-scrolling: touch;
        }

        /* ===== Encabezado gris rectangular ===== */
        .header{
          display:flex; align-items:center; justify-content:space-between;
          background:#F8FAFB;
          margin: -12px -12px 12px;
          padding: 12px;
          border-top-left-radius:12px;
          border-top-right-radius:12px;
          border-bottom:1px solid #E6EDFF;
        }
        .header-left{ display:flex; align-items:center; gap:10px; }

        .header-icon{
          position:relative; display:inline-flex; align-items:center; justify-content:center;
          width:24px; height:24px; border-radius:6px;
          color:#317BFF;
          font-size:18px;
        }
        .header-icon .dot{
          position:absolute; top:5px; right:2px; width:6px; height:6px; border-radius:50%;
          background:#317BFF;
        }
        .header-title{
          color:#317BFF;
          font-weight:700;
          font-size:18px;
        }

        /* botón + */
        .add-lista-btn{
          width:28px; height:28px; border-radius:8px;
          display:flex; align-items:center; justify-content:center;
          background:#E9F2FF;
          border:1px solid #E6EDFF;
          color:#007DFC;
          font-size:18px; line-height:1;
          cursor:pointer;
          padding:0;
          outline:none;
          box-shadow:none;
          -webkit-tap-highlight-color: transparent;
          transition:none;
        }
        .add-lista-btn:hover,
        .add-lista-btn:focus,
        .add-lista-btn:active,
        .add-lista-btn:focus-visible{
          background:#E9F2FF;
          color:#007DFC;
          border-color:#E6EDFF;
          outline:none;
          box-shadow:none;
          filter:none;
          transform:none;
        }

        /* ===== Buscador con icono de lupa CENTRADO ===== */
        .search-wrap{
          position: relative;
          margin-bottom: 10px;
        }
        .search-icon{
          position:absolute;
          top:48%;
          left:12px;
          transform: translateY(-75%);
          display:flex;
          align-items:center;
          justify-content:center;
          pointer-events:none;
        }
        .search-icon i{
          font-size:16px;
          color:#9AA3B2;
          line-height:1;
        }
        .input-lista{
          width:100%;
          box-sizing:border-box;
          padding:10px 12px 10px 40px;
          border:1px solid #E6EDFF;
          border-radius:10px;
          outline:none;
          background:#fff;
        }
        .input-lista::placeholder{
          color:#9AA3B2;
        }
        .input-lista:focus{ box-shadow:0 0 0 3px rgba(49,123,255,.08); }

        /* listado */
        .lista-nombres{ list-style:none; margin:0; padding:0; }
        .lista-nombres li{ padding:8px 6px; border-radius:8px; }
        .lista-nombres li:hover{ background:#f8fafc; }
        .lista-nombres li span{ white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:70%; }
        .custom-dropdown{ position:absolute; right:0; top:100%; background:#fff; border:1px solid #E6EDFF; border-radius:8px; padding:6px; z-index:5; }
        .dd-item{ display:flex; align-items:center; gap:6px; padding:6px 10px; border:0; background:#fff; width:100%; text-align:left; border-radius:6px; cursor:pointer; }
        .dd-item:hover{ background:#f4f8ff; }
        .dd-item--danger{ color:#b91c1c; }

        /* selector de color (input color horizontal) */
        .color-picker-row{
          display:flex;
          align-items:center;
          gap:12px;
          margin:10px 0 12px;
        }
        .color-input{
          -webkit-appearance:none;
          border:none;
          padding:0;
          width:180px;
          height:26px;
          background:transparent;
          cursor:pointer;
        }
        .color-input::-webkit-color-swatch{
          border-radius:999px;
          border:1px solid #cbd5e1;
        }
        .color-input::-moz-color-swatch{
          border-radius:999px;
          border:1px solid #cbd5e1;
        }
        .color-preview-dot{
          width:18px;
          height:18px;
          border-radius:999px;
          border:2px solid rgba(15,23,42,0.25);
        }

        /* botón de cerrar del modal (la X) */
        .modal-close{
          position:absolute;
          top:8px;           /* más pegado a la esquina */
          right:10px;
          border:none;
          background:transparent;
          cursor:pointer;
          padding:4px;
          line-height:1;
          font-size:18px;
        }

        /* footer total */
        .sidebar-total{
          position:relative;
          flex:0 0 auto;
          margin-top:10px;
          padding:10px 12px 8px 12px;
          background:#fff;
          border-radius:10px;
          display:flex;
          align-items:center;
          justify-content:space-between;
          font-family: Montserrat, sans-serif;
        }
        .sidebar-total::before{
          content:"";
          position:absolute; left:12px; right:12px; top:-8px;
          height:1px; background:#E6EDFF;
        }
        .sidebar-total .label{
          color:#0f172a; font-size:12.5px; font-weight:700; line-height:1.1; white-space:nowrap;
        }
        .sidebar-total .value{
          color:#0f172a; font-size:15px; font-weight:600; line-height:1.1; white-space:nowrap;
          font-variant-numeric: tabular-nums;
        }
      `}</style>

      {/* header */}
      <div className="header">
        <div className="header-left">
          <span className="header-icon" aria-hidden="true">
            <i className="bi bi-list" />
            <span className="dot" />
          </span>
          <span className="header-title">Proveedor</span>
        </div>

        <button
          onClick={openModal}
          title="Agregar carpeta"
          className="add-lista-btn"
          aria-label="Agregar carpeta"
        >
          +
        </button>
      </div>

      {/* cuerpo scrolleable: buscador + listado */}
      <div className="sidebar-body">
        {/* Buscador con lupa */}
        <div className="search-wrap">
          <span className="search-icon" aria-hidden="true">
            <i className="bi bi-search" />
          </span>
          <input
            className="input-lista"
            placeholder="Buscar Proveedor"
            aria-label="Buscar proveedor"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </div>

        <ul className="lista-nombres">
          {listasFiltradas.map((lista) => (
            <li
              key={lista.id}
              onClick={() => onSelectLista(lista)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <span style={{ display: "flex", alignItems: "center" }}>
                {/* carpeta pintada con el color elegido */}
                <i
                  className="bi bi-folder2-open"
                  style={{
                    marginRight: 8,
                    color: "#ffffff",
                    backgroundColor: lista.color || "#CBD5E1",
                    borderRadius: 8,
                    padding: 4,
                    fontSize: 16,
                  }}
                />
                {lista.nombre}
              </span>
              <div style={{ position: "relative" }}>
                <i
                  className="bi bi-three-dots"
                  style={{ cursor: "pointer", padding: 5 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(menuOpen === lista.id ? null : lista.id);
                  }}
                />
                <div
                  className="custom-dropdown"
                  style={{ display: menuOpen === lista.id ? "block" : "none" }}
                >
                  <button
                    className="dd-item"
                    onClick={() => {
                      editarLista(lista);
                      setMenuOpen(null);
                    }}
                  >
                    <i className="bi bi-pencil-fill" /> Editar
                  </button>
                  <button
                    className="dd-item dd-item--danger"
                    onClick={() => {
                      eliminarLista(lista.id);
                      setMenuOpen(null);
                    }}
                  >
                    <i className="bi bi-trash-fill" /> Eliminar
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* footer con total */}
      <div className="sidebar-total">
        <span className="label">Total MXN</span>
        <span className="value">{formatCurrency(totalCarpetas)}</span>
      </div>

      {/* modal */}
      <Modal isOpen={modalOpen} onRequestClose={closeModal} style={modalStyles}>
        <button className="modal-close" onClick={closeModal}>
          <i className="bi bi-x-lg" />
        </button>

        <h2 style={{ textAlign: "center", marginBottom: 16 }}>
          {editId ? "Editar Carpeta" : "Crear Nueva Carpeta"}
        </h2>

        <label style={{ display: "block", marginBottom: 6 }}>
          Nombre de la carpeta:
        </label>
        <input
          type="text"
          value={listaName}
          onChange={(e) => setListaName(e.target.value)}
          style={modalStyles.input}
        />

        {/* selector de color */}
        <label style={{ display: "block", marginBottom: 4, marginTop: 10 }}>
          Color de la carpeta:
        </label>
        <div className="color-picker-row">
          <input
            type="color"
            className="color-input"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
          <span
            className="color-preview-dot"
            style={{ backgroundColor: color }}
          />
        </div>

        {/* fila de botones pegada al fondo (solo margen pequeño) */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 12,
          }}
        >
          <button
            onClick={closeModal}
            style={{
              padding: "8px 16px",
              background: "#ddd",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={guardarLista}
            style={{
              padding: "8px 16px",
              background: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: 4,
            }}
          >
            {editId ? "Guardar cambios" : "Crear carpeta"}
          </button>
        </div>
      </Modal>
    </div>
  );
}
